import React from 'react';
import { Plus } from 'lucide-react';

const BluechipEcosystem = () => {
  return (
    <section className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Built on the best. Invisible by design.</span>{' '}
            <span className="text-gray-500 font-normal">Industry-leading protocols, without the complexity.</span>
          </h2>
        </div>

        {/* Three Column Layout with Dividers */}
        <div className="grid lg:grid-cols-3">
          {/* Application Layer */}
          <div className="lg:pr-12 lg:border-r lg:border-gray-200 space-y-4">
            {/* Layer Badge */}
            <div className="flex justify-center mb-6">
              <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                Application Layer
              </span>
            </div>

            {/* StageX */}
            <a 
              href="https://stagex.space/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/b6z8aocx_StageX.png"
                    alt="StageX"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-gray-900 font-semibold">StageX</h3>
              </div>
            </a>

            {/* QuickSync Me */}
            <a 
              href="https://quicksync.me/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                  <img 
                    src="/quicksync-me-icon.png"
                    alt="QuickSync Me"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-gray-900 font-semibold">QuickSync Me</h3>
              </div>
            </a>

            {/* Build Yours */}
            <a 
              href="https://docs.quicksync.me/qs-framework"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border-2 border-dashed border-gray-300 rounded-xl p-5 hover:border-gray-400 transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                  <Plus className="w-5 h-5 text-gray-600" />
                </div>
                <h3 className="text-gray-600 font-semibold group-hover:text-gray-900 transition-colors">Build Yours</h3>
              </div>
            </a>
          </div>

          {/* Protocol Layer - Center */}
          <div className="lg:px-12 lg:border-r lg:border-gray-200 space-y-4 mt-8 lg:mt-0">
            {/* Layer Badge */}
            <div className="flex justify-center mb-6">
              <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                Protocol Layer
              </span>
            </div>

            {/* QS Framework - Main Card */}
            <a 
              href="https://docs.quicksync.me/qs-framework"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border-2 border-gray-300 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer"
            >
              {/* Logo */}
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 rounded-xl flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/wg56q37t_Framework%20QS.png"
                    alt="QS Framework"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-gray-900 font-bold text-xl text-center mb-3">
                QS Framework
              </h3>

              {/* Description */}
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                The walletless + privacy logic layer. Handles identity, interactions, value exchange, and encrypted communication
              </p>
            </a>
          </div>

          {/* Blockchain & Data Layer */}
          <div className="lg:pl-12 space-y-4 mt-8 lg:mt-0">
            {/* Layer Badge */}
            <div className="flex justify-center mb-6">
              <span className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                Blockchain & Data
              </span>
            </div>

            {/* Ethereum */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_9c600170-5d16-447d-bfe0-1d0de6d7d09f/artifacts/q1dsq87m_51fqZW7YPfL._AC_UF1000%2C1000_QL80_.jpg"
                    alt="Ethereum"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
                <h3 className="text-gray-900 font-semibold">Ethereum</h3>
              </div>
            </div>

            {/* Filecoin */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_collab-hub-62/artifacts/knkungw8_Filecoin.svg.png"
                    alt="Filecoin"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-gray-900 font-semibold">Filecoin</h3>
              </div>
            </div>

            {/* IPFS */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_collab-hub-62/artifacts/60kric36_Ipfs-logo-1024-ice-text.png"
                    alt="IPFS"
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-gray-900 font-semibold">IPFS</h3>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BluechipEcosystem;
